/**
 * `@dxf-ai-editor/cad-core` — the AABB of the shape an `LWPOLYLINE` is
 * actually DRAWN as: its width BAND where it declares one, its centreline
 * (bulge arcs included) where it does not (@codex PR #785 round 11, F1).
 *
 * ONE function, because there are THREE callers and they must not learn
 * different subsets of the rules — the same shape rounds 8–10 closed for the
 * heavy `POLYLINE` in {@link heavyPolylineDrawnBounds}:
 *
 *   - `./bounds.ts`' `"LWPOLYLINE"` arm — a top-level LWPOLYLINE's box, the
 *     one `getSelectGeometry` hands out and the one `apps/web`'s
 *     `state/entityMetadataIndex.ts` publishes to the spatial index on the
 *     CACHE-DIRECT path;
 *   - `./blockDefinitionWalk.ts`' `childEntityBounds` `"LWPOLYLINE"` arm — the
 *     same polyline as a BLOCK-DEFINITION child, whose box is then run through
 *     the INSERT affine (leaving this one behind is exactly round 9's F2, one
 *     entity kind later);
 *   - through the first of those, every `getEntityBounds` consumer.
 *
 * # The defect
 *
 * `drawing-model`'s `entityModelBounds` → `lwpolylineBounds` walks the
 * per-span `bulgeSegmentBounds` boxes, so it frames the CENTRELINE exactly,
 * arcs and all — and reads neither group 43 nor groups 40 / 41. An LWPOLYLINE
 * that declares a width is not stroked along that centreline at ALL: the
 * canvas (`batchedLayer.ts::widePolylineLineVertices` over the `widths` the
 * typed adapter emits) and the vector plot both draw the mitred,
 * square-capped BAND. Constant width 4 on `(0,0) → (10,0)`:
 *
 *     model box      {minX: 0, minY:  0, maxX: 10, maxY: 0}   reported
 *     drawn ribbon   {minX: 0, minY: -2, maxX: 10, maxY: 2}   true
 *
 * That is geometry LOSS rather than a slack box wherever the box is a
 * PREFILTER. On the cache-direct branch (streaming import, DXF >= 2 MB) this
 * box is the only description of the entity the rbush holds, and
 * `lod/visibleSet.ts::computeVisibleSet` begins at `spatial.intersect(viewport)`
 * and resolves only the ids that come back — so a viewport over `y ∈ [1, 3]`
 * cuts the ribbon, misses the centreline at `y ≡ 0`, and the batcher that DOES
 * understand widths is never reached. `apps/web`'s
 * `scene3d/scene/__tests__/cacheDirectWideLwpolylineBand.test.ts` drives that
 * end to end.
 *
 * # The frame: UNLIFTED, deliberately
 *
 * An LWPOLYLINE's group-10/20 vertices are OCS coordinates in the plane of its
 * group-210 extrusion, exactly like a 2D heavy POLYLINE's — but
 * `apps/web/src/app/scene3d/adapters/typed/lwpolylineFromEntity.ts` does NOT
 * lift them (it feeds raw x/y with `z = elevation`), and neither does
 * `lwpolylineBounds`. So the band is built here in the SAME unlifted frame,
 * with NO `extrusion` argument passed to {@link widePolylineBandBounds}: this
 * box must agree with what the renderer draws, and lifting it alone would move
 * the record off the drawn geometry — manufacturing the very disappearing-
 * geometry bug it exists to close. The LWPOLYLINE OCS lift is a RENDERER gap
 * that has to be closed there first; it stays a DECLARED gap, pinned at its
 * measured value by `apps/web`'s
 * `state/__tests__/entityMetadataIndex.lwpolylineBulgeBounds.test.ts`.
 *
 * # FOUND BUT NOT FIXED, with the measurement rather than an impression
 *
 * `./selectLeaf.ts`' `"LWPOLYLINE"` arm is still CENTRELINE-only. For the same
 * constant-width-4 ribbon `getSelectGeometry` now reports
 *
 *     bounds           {minX: 0, minY: -2, maxX: 10, maxY: 2}   the band
 *     select points    [(0, 0), (10, 0)]                        the centreline
 *     select segments  [(0, 0) → (10, 0)]
 *
 * so the points are a strict SUBSET of the box — the SAFE direction
 * `blockSelectGeometry.ts` states as its invariant, and the direction round 9's
 * F2 found violated for the heavy POLYLINE. Nothing is dropped by this arm; an
 * exact picker simply tests a thinner shape than the canvas draws, so a click
 * on the band's edge alone misses. (The CANVAS-side window / crossing test does
 * NOT: it reads the renderer's own band through `RenderableEntity` —
 * `scene3d/raycast/windowSelect.drawnPolyline.test.ts`.) Closing it means
 * giving the LWPOLYLINE select arm its own drawn-shape walk, which is the
 * `heavyPolylineDrawnShape.ts` shape a second time and a separate change.
 *
 * # Why the walk is GATED on the authored width
 *
 * Same reason and the same shape as {@link heavyPolylineDrawnBounds}': this
 * runs per entity while the metadata index is built for a 685k-entity drawing
 * (`.claude/skills/scene3d-virtualization`). Measured on this repo, 20 000
 * LWPOLYLINEs per shape, median of 5 runs — `model` is the pre-fix
 * `entityModelBounds` call alone, `gated` is this function, `ungated` is the
 * same function with the width gate removed:
 *
 *                                  model      gated              ungated
 *   no width, straight,  4 verts   1.19 us    0.96 us   0.81x    3.24 us   2.73x
 *   no width, bulged,    4 verts   1.18 us    1.20 us   1.01x   48.15 us  40.81x
 *   no width, straight, 20 verts   1.02 us    1.40 us   1.37x   15.51 us  15.16x
 *   width 4, straight,   4 verts   0.34 us    3.70 us  10.73x    3.83 us  11.14x
 *   width 4, bulged,     4 verts   0.95 us   47.30 us  49.75x   48.34 us  50.85x
 *   width 4, straight,  20 verts   1.23 us   18.05 us  14.69x   17.67 us  14.38x
 *
 * The gate is the whole difference on the common case: a width-LESS entity
 * pays 0.81x-1.37x instead of 2.73x-40.81x, the residual being
 * {@link lwpolylineWidths}' own per-entity array. An entity that DOES declare
 * a width pays 10.7x-49.8x, and there is no way around that — the band IS the
 * geometry, and a box that does not contain it loses the entity.
 *
 * The skip is exact rather than an approximation traded for speed:
 * `widePolylineBandLoops` short-circuits an all-zero width list
 * (`polylineWidthOutline.ts::allWidthsZero` — the band IS the centreline), so
 * for such an entity the union term could only ever be a subset of the box
 * `lwpolylineBounds` already computes from the EXACT per-span arc extrema.
 * `./lwpolylineDrawnBounds.property.test.ts`'s P15 pins that as a property
 * rather than leaving it a claim.
 */

import {
  ALWAYS_INCLUDE,
  entityModelBounds,
  hasPolylineWidth,
  lwpolylineWidths,
  type LwpolylineEntity,
  type Vec3,
} from "@dxf-ai-editor/drawing-model";

import type { Aabb } from "./common";
import { unionBounds } from "./common";
import { widePolylineBandBounds } from "./polylineWidthBand";

/**
 * The WCS (XY-projected, UNLIFTED — see the module doc) box of an
 * `LWPOLYLINE`: its DRAWN band where the entity declares a width, UNION'd with
 * the model box `entityModelBounds` computes for the same entity.
 *
 * The model box is union'd rather than replaced because it is the arm that
 * owns the EXACT bulge extrema (the band is a 64-per-turn tessellation) and
 * the degenerate fallbacks, and a bounds box must never shrink.
 *
 * `null` only when the entity has no model box at all.
 */
export function lwpolylineDrawnBounds(entity: LwpolylineEntity): Aabb | null {
  // DOCUMENTED EQUIVALENT MUTANT (targeted mutation testing), identical in
  // kind to `./heavyPolylineDrawnBounds.ts`' first one:
  //   file:line       this file, the `model === ALWAYS_INCLUDE` half of the
  //                   guard below
  //   mutator         ConditionalExpression → `false`
  //   why equivalent  `entityModelBounds`' `"LWPOLYLINE"` arm is
  //                   `lwpolylineBounds`, typed `EntityViewBounds | null`; the
  //                   never-drop sentinel is produced only by the
  //                   BLOCK-reference arms, which an LWPOLYLINE can never take
  //   the invariant   no LWPOLYLINE resolves to `ALWAYS_INCLUDE`
  //   kept because    the local `model` is typed `ResolvedEntityBounds` and
  //                   narrowing it is what makes the object literal below
  //                   type-check
  // The OTHER three mutants on this line are NOT equivalent and are killed by
  // this file's two vertex-less records: `→ false` on the whole condition,
  // `→ false` on the `model === null` half, and `||` → `&&` each spread
  // `model.minX` out of `null` and throw.
  const model = entityModelBounds(entity);
  const base =
    model === null || model === ALWAYS_INCLUDE
      ? null
      : { minX: model.minX, minY: model.minY, maxX: model.maxX, maxY: model.maxY };

  // THE WIDTH GATE (see the module doc for the measurements and the proof).
  // `lwpolylineWidths` is `drawing-model`'s ONE reading of which width CHANNEL
  // the entity is in — the group-43 constant, or the per-vertex 40 / 41 pair
  // that makes 43 "not used" for the whole entity — and it is the SAME call
  // the typed adapter makes to build the geometry the canvas strokes, so this
  // branch cannot disagree with the band it is skipping.
  const widths = lwpolylineWidths(entity);
  if (!hasPolylineWidth(widths)) return base;

  const z = Number.isFinite(entity.elevation) ? entity.elevation : 0;
  const points: Vec3[] = entity.vertices.map((v) => ({ x: v.x, y: v.y, z }));
  const bulges = entity.vertices.map((v) => v.bulge);

  // NO `extrusion` argument — the unlifted frame, matching the renderer (see
  // the module doc). The mitred outer corners come with the loops, which is
  // the whole reason the box is taken from them rather than padded.
  const drawn = widePolylineBandBounds(points, entity.closed, bulges, widths);

  if (!drawn) return base;
  return base ? unionBounds(base, drawn) : drawn;
}
